import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, LogIn } from "lucide-react";
import { memberApi } from "../api/member";
import { pageRoutes } from "../utils";

export default function MemberLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    if (status === "loading") {
      return;
    }

    setStatus("loading");
    setErrorMessage("");

    try {
      await memberApi("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      const destination =
        location.state?.from?.pathname || pageRoutes.MemberDashboard;
      navigate(destination, { replace: true });
    } catch (error) {
      if (error?.code === "EMAIL_NOT_VERIFIED") {
        sessionStorage.setItem("pendingVerificationEmail", email);
        sessionStorage.setItem(
          "pendingVerificationMessage",
          "Please enter the 4-digit code sent to your email to finish registration."
        );
        navigate(pageRoutes.MemberVerifyEmail);
        return;
      }
      setStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to sign in."
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-16 px-4">
      <div className="mx-auto max-w-md rounded-3xl border border-[#C9A227]/20 bg-white p-8 shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#A88520]">
          Member Portal
        </p>
        <h1 className="mt-3 font-serif-elegant text-3xl font-bold text-[#722F37]">
          Sign in to your member dashboard
        </h1>
        <p className="mt-3 text-sm text-gray-600">
          View your membership status, profile, and secure Stripe payment history.
        </p>

        {errorMessage && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p>{errorMessage}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              autoComplete="email"
              required
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              autoComplete="current-password"
              required
            />
          </label>

          <button
            type="submit"
            disabled={status === "loading"}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-70"
          >
            {status === "loading" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogIn className="h-4 w-4" />
            )}
            Sign in
          </button>
        </form>

        <div className="mt-5 text-center text-sm">
          <Link className="font-semibold text-[#722F37] underline" to={pageRoutes.ForgotPassword}>
            Forgot password?
          </Link>
        </div>

        <p className="mt-4 text-center text-sm text-gray-600">
          Need an account?{" "}
          <Link className="font-semibold text-[#722F37] underline" to={pageRoutes.MemberPortal}>
            Register here
          </Link>
        </p>
      </div>
    </div>
  );
}
